import * as Header from '-/Custom/Header';
import * as Placeholder from '-/Custom/Placeholder';
import { Stack, useRouter } from 'expo-router';
import { useMutation } from 'urql';
import { ChatHomeListItemFragment } from '@/components/Chat/ChatHomeListItem.utils';
import {
  CHAT_LIST_PAGE_SIZE,
  GetChatHomePage,
  getChatCursorWhere,
} from '@/components/Chat/ChatListQuery';
import {
  SetChatHidden,
  UpsertChatPersonPreference,
} from '@/components/Chat/ChatPersonPreference.utils';
import Icon from '@/components/Unistyles/Icon';
import List from '@/components/Unistyles/List';
import { readFragment } from '@/gql/graphql';
import useCursorPagination from '@/hooks/useCursorPagination';
import useUserId from '@/hooks/useUserId';
import { openDrawer } from '@/stores/useRootStore';
import ChatCreateMenu from './components/ChatCreateMenu';
import ChatHomeListItem from './components/ChatHomeListItem';

const TITLE = 'Messages';

export default function ChatHome() {
  const { push } = useRouter();
  const userId = useUserId();
  const { data, fetching, fetchNextPage } = useCursorPagination({
    query: GetChatHomePage,
    baseVariables: {
      limit: CHAT_LIST_PAGE_SIZE,
      where: { is_hidden: { _eq: false } },
    },
    getNextPageVariables: (currentData) => {
      const lastChat = currentData?.chat_home.at(-1);
      if (!lastChat) {
        return null;
      }

      return {
        limit: CHAT_LIST_PAGE_SIZE,
        where: {
          is_hidden: { _eq: false },
          ...getChatCursorWhere(lastChat),
        },
      };
    },
  });
  const [, setChatHidden] = useMutation(SetChatHidden);
  const [, upsertChatPersonPreference] = useMutation(
    UpsertChatPersonPreference,
  );

  const chatPage = data?.chat_home ?? [];
  const chats = chatPage.filter(
    (chat) => readFragment(ChatHomeListItemFragment, chat).is_hidden === false,
  );

  const hideChat = async (chatId: string) => {
    await setChatHidden({
      chatId,
      personId: userId,
      isHidden: true,
    });
  };

  const updateChatSilenced = async (chatId: string, isSilenced: boolean) => {
    await upsertChatPersonPreference({
      chatId,
      personId: userId,
      notificationPreference: isSilenced ? 'none' : 'all',
    });
  };

  const headerLeft = () => (
    <Header.Start>
      <Header.Button
        accessibilityLabel="Open navigation"
        testID="OpenNavigationButton"
        onPress={openDrawer}
      >
        <Icon name="menu-outline" size="3xl" />
      </Header.Button>
    </Header.Start>
  );

  const headerRight = () => (
    <Header.End>
      <Header.ButtonGroup>
        <Header.Button
          accessibilityLabel="Hidden chats"
          onPress={() => {
            push('/chat/hidden');
          }}
          testID="ChatHomeHiddenChatsButton"
        >
          <Icon name="eye-off-outline" size="3xl" />
        </Header.Button>
        <ChatCreateMenu />
      </Header.ButtonGroup>
    </Header.End>
  );

  const headerTitle = () => {
    return (
      <Header.TitleContainer>
        <Header.Title>Messages</Header.Title>
      </Header.TitleContainer>
    );
  };

  return (
    <>
      <Stack.Screen
        options={{
          headerLeft,
          headerRight,
          title: TITLE,
          headerTitle,
          headerShadowVisible: false,
        }}
      />
      <List
        data={chats}
        fetching={fetching}
        keyboardScroll={false}
        onEndReached={fetchNextPage}
        onEndReachedThreshold={0.5}
        onStartReachedThreshold={0}
        renderItem={({ item }) => (
          <ChatHomeListItem
            fragment={item}
            onHide={hideChat}
            onSetSilenced={updateChatSilenced}
          />
        )}
        keyExtractor={(item) => readFragment(ChatHomeListItemFragment, item).id}
        ListEmptyComponent={
          <Placeholder.Container>
            <Placeholder.Icon name="chatbubble-outline" />
            <Placeholder.Text>You don't have any chats yet.</Placeholder.Text>
            <Placeholder.Text>
              Send a direct message or create a group chat using the + button
              above.
            </Placeholder.Text>
          </Placeholder.Container>
        }
      />
    </>
  );
}
